import {
  faCalendar,
  faCalendarCheck,
  faCalendarPlus,
  faCog,
  faDownload,
  faIdCard,
  faMapMarkerAlt,
  faPlus,
  faSearch,
  faStar,
  faTrash,
  faUserCheck,
  faUsers,
  faUsersSlash,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useState, useEffect, useMemo } from 'react';
import NumberFlow from '@number-flow/react';
import cn from 'classnames';
import { getGroup, deleteGroups, removeMemberFromGroup } from '@redux/features/groupsSlice';
import { addNotification } from '@redux/features/notificationsSlice';
import { getEntries } from '@redux/features/entriesSlice';
import { getEntityLink } from 'helpers/getEntityLink';
import { formatDate } from 'helpers/formatDate';
import { BackButton } from 'components/BackButton';
import { Button } from 'components/Button';
import { Input } from 'components/Input';
import { GroupEntries } from './GroupEntries';
import { AddMembers } from './AddMembers';
import { EditGroup } from './EditGroup';
import styles from './GroupPage.scss';

export const GroupPage = () => {
  const { entries } = useSelector((state) => state.entries);
  const { groups } = useSelector((state) => state.groups);
  const { groupID } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [searchMembers, setSearchMembers] = useState('');
  const [isEditGroupModalOpen, setIsEditGroupModalOpen] = useState(false);
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);

  useEffect(() => {
    if (!groups.some((g) => g.id === Number(groupID))) {
      dispatch(getGroup(groupID));
    }
    if (!entries.some((e) => e.groupID === Number(groupID))) {
      dispatch(getEntries());
    }
  }, []);

  const group = useMemo(() => groups.find((g) => g.id === Number(groupID)), [groups, groupID]);

  useEffect(() => {
    if (groups.length > 0 && !group?.members) {
      dispatch(getGroup(groupID));
    }
  }, [groups, group, groupID]);

  const currentMonthGroupEntries = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    return entries.filter((e) => {
      if (e.group?.id !== Number(groupID)) return false;

      const entryDate = new Date(e.date);
      const entryMonth = entryDate.getMonth();
      const entryYear = entryDate.getFullYear();

      return entryMonth === currentMonth && entryYear === currentYear;
    });
  }, [entries, groupID]);

  const formattedGroupEntries = useMemo(
    () =>
      entries
        .filter((e) => e.group?.id === Number(group.id))
        .map((entry) => ({
          ...entry,
          locationName: entry.location?.name,
          hallName: entry.hall?.name,
          createdAt: entry.metadata?.createdAt,
        })),
    [entries, group]
  );

  const filteredMembers = useMemo(() => {
    if (group?.members) {
      return group.members.filter(
        (member) =>
          searchMembers === '' ||
          member.name.toLowerCase().includes(searchMembers.toLowerCase()) ||
          member.surname.toLowerCase().includes(searchMembers.toLowerCase())
      );
    }
  }, [group, searchMembers]);

  const handleDeleteMember = (e, memberID) => {
    e.stopPropagation();
    dispatch(removeMemberFromGroup({ groupID: group.id, clientID: Number(memberID) }));
  };

  const handleExportData = () => {
    dispatch(
      addNotification({
        id: Date.now(),
        title: 'Функціонал знаходиться у розробці',
        text: 'Слідкуйте за оновленнями',
        type: 'info',
      })
    );
  };

  const handleDeleteGroup = async () => {
    const result = await dispatch(deleteGroups([group.id]));

    if (result?.meta.requestStatus === 'fulfilled') {
      navigate(getEntityLink('database/groups'));
    }
  };

  if (!group?.members) return null;

  return (
    <div className={styles.groupManagement}>
      <BackButton text='Список груп' path={getEntityLink('database/groups')} />
      <div className={cn(styles.header, styles.card)}>
        <div className={styles.group}>
          <div className={styles.groupTitle}>{group.name}</div>

          <div className={styles.groupInfo}>
            <div className={styles.infoItem}>
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              <span>{group.location?.name}</span>
            </div>
            <div className={styles.infoItem}>
              <FontAwesomeIcon icon={faCalendar} />
              <span>Створено: {formatDate(group.createdAt, 'UA')}</span>
            </div>
            {group?.isClub && (
              <div className={styles.infoItem}>
                <FontAwesomeIcon icon={faStar} />
                <span>Клубна група</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.mainContent}>
        <div className={cn(styles.members, styles.card)}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Учасники групи</h2>
            <Input
              className={styles.searchInput}
              icon={faSearch}
              placeholder='Пошук учасників...'
              value={searchMembers}
              onChange={(e) => setSearchMembers(e.target.value)}
            />
            <Button className={styles.addMemberBtn} onClick={() => setIsAddMemberModalOpen(true)} smallStyle>
              Додати
            </Button>
          </div>

          <div className={styles.membersList}>
            {filteredMembers?.length > 0 ? (
              filteredMembers.map((member) => (
                <div
                  key={member.id}
                  className={styles.memberCard}
                  onClick={() => navigate(getEntityLink(`database/clients/${member.id}`))}
                >
                  <div className={styles.memberInfo}>
                    <div className={styles.memberAvatar}>
                      {member.surname[0]}
                      {member.name[0]}
                    </div>
                    <div>
                      <h4 className={styles.memberName}>
                        {member.surname} {member.name}
                        {member.subscription && (
                          <FontAwesomeIcon
                            className={cn(styles.subscriptionIcon, {
                              [styles.star]: member.subscription?.type === 'membership',
                            })}
                            icon={member.subscription?.type === 'membership' ? faStar : faIdCard}
                          />
                        )}
                      </h4>
                      <span className={styles.memberJoinDate}>Долучено: {formatDate(member.joinDate, 'UA')}</span>
                    </div>
                  </div>
                  <Button
                    className={cn(styles.actionBtn, styles.delete)}
                    onClick={(e) => handleDeleteMember(e, member.id)}
                    title='Видалити'
                    noStyle
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                </div>
              ))
            ) : (
              <div className={styles.empty}>
                <FontAwesomeIcon icon={faUsersSlash} className={styles.icon} />
                <h3>Учасників не знайдено</h3>
                <p>Додайте першого учасника групи</p>
              </div>
            )}
          </div>
        </div>

        <div className={styles.sidebar}>
          <div className={styles.card}>
            <h3 className={styles.sectionTitle}>Статистика</h3>
            <div className={styles.statsList}>
              <div className={styles.stat}>
                <div className={styles.statIcon}>
                  <FontAwesomeIcon icon={faUsers} />
                </div>
                <div className={styles.statNumber}>
                  <NumberFlow value={group?.members.length} />
                </div>
                <div className={styles.statLabel}>Учасників</div>
              </div>

              <div className={styles.stat}>
                <div className={styles.statIcon}>
                  <FontAwesomeIcon icon={faCalendarCheck} />
                </div>
                <div className={styles.statNumber}>
                  <NumberFlow value={currentMonthGroupEntries?.length} />
                </div>
                <div className={styles.statLabel}>Занять цього місяця</div>
              </div>

              <div className={styles.stat}>
                <div className={styles.statIcon}>
                  <FontAwesomeIcon icon={faUserCheck} />
                </div>
                <div className={styles.statNumber}>
                  <NumberFlow value='0' />%
                </div>
                <div className={styles.statLabel}>Відвідуваність</div>
              </div>
            </div>
          </div>
          <div className={styles.card}>
            <h3 className={styles.sectionTitle}>Швидкі дії</h3>
            <div className={styles.actionList}>
              <button className={styles.actionItem} onClick={() => navigate(getEntityLink('schedule'))}>
                <div className={cn(styles.actionIcon, styles.schedule)}>
                  <FontAwesomeIcon icon={faCalendarPlus} />
                </div>
                <div className={styles.actionText}>
                  <h4>Запланувати заняття</h4>
                  <p>Створити нове заняття для групи</p>
                </div>
              </button>

              <button className={styles.actionItem} onClick={() => setIsEditGroupModalOpen(true)}>
                <div className={cn(styles.actionIcon, styles.settings)}>
                  <FontAwesomeIcon icon={faCog} />
                </div>
                <div className={styles.actionText}>
                  <h4>Налаштування групи</h4>
                  <p>Змінити параметри групи</p>
                </div>
              </button>

              <button className={styles.actionItem} onClick={handleExportData}>
                <div className={cn(styles.actionIcon, styles.export)}>
                  <FontAwesomeIcon icon={faDownload} />
                </div>
                <div className={styles.actionText}>
                  <h4>Експорт даних</h4>
                  <p>Завантажити звіт по групі</p>
                </div>
              </button>

              <button className={styles.actionItem} onClick={handleDeleteGroup}>
                <div className={cn(styles.actionIcon, styles.delete)}>
                  <FontAwesomeIcon icon={faTrash} />
                </div>
                <div className={styles.actionText}>
                  <h4>Видалити групу</h4>
                  <p>Остаточно видалити групу</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      <GroupEntries entries={formattedGroupEntries} />

      <EditGroup group={group} isOpen={isEditGroupModalOpen} setIsOpen={setIsEditGroupModalOpen} />
      <AddMembers group={group} isOpen={isAddMemberModalOpen} setIsOpen={setIsAddMemberModalOpen} />
    </div>
  );
};
